import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteContent } from "../context/SiteContent";
import { fetchClients, getLogoUrl } from "../lib/api";
import { clients as fallbackClients } from "../data/mock";
import { Button } from "../components/ui/button";
import WorldMap from "../components/WorldMap";
import NewsTicker from "../components/NewsTicker";
import ImageCarousel from "../components/ImageCarousel";
import SeoHead from "../components/SeoHead";
import {
  Globe, FileText, Link2, Building2, TrendingUp, Briefcase,
  ArrowLeftRight, Monitor, Users, Leaf, BarChart3, ArrowRight
} from "lucide-react";

const iconMap = { Globe, FileText, Link2, Building2, TrendingUp, Briefcase, ArrowLeftRight, Monitor, Users, Leaf, BarChart3 };

const Home = () => {
  const { personalInfo, heroTagline, expertise, stats, images, heroImages, socialLinks } = useSiteContent();
  const [clients, setClients] = useState(fallbackClients);

  useEffect(() => {
    fetchClients().then((d) => d.length > 0 && setClients(d)).catch(() => {});
  }, []);

  const sameAs = [
    personalInfo.social.linkedin,
    ...(socialLinks || []).map((s) => s.url),
  ].filter(Boolean);

  const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: personalInfo.name,
    jobTitle: personalInfo.title,
    description: personalInfo.shortBio,
    email: personalInfo.contact.emailWork || personalInfo.contact.email,
    url: "https://abhilash.com",
    address: { "@type": "PostalAddress", addressLocality: personalInfo.contact.address },
    sameAs,
    knowsAbout: ["International Development", "Trade Policy", "Strategy Consulting", "Public-Private Partnerships", "South-South Cooperation"],
  };

  return (
    <div>
      <SeoHead
        title="Abhilash Puljal | Strategy Advisor | India, Africa & Asia"
        description="Cross-border strategist advising public and private sector clients across India, Africa and Asia on trade strategy, investment, and South-South cooperation."
        path="/"
        keywords="Abhilash Puljal, strategy advisor, India Africa corridor, India Asia corridor, cross-border investment, South-South cooperation, trade facilitation, AfCFTA, value chain advisory, trilateral development cooperation, LSE India, strategy consultant"
        jsonLd={personJsonLd}
      />
      {/* Hero */}
      <section className="min-h-[88vh] flex items-center bg-[#FAF8F4]" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 w-full py-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 animate-fade-up">
              <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-6" data-testid="hero-tagline">
                {heroTagline}
              </p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-[#1C2B3A] leading-tight mb-6">{personalInfo.name}</h1>
              <div className="w-20 h-[2px] bg-[#B8945A] mb-6"></div>
              <p className="font-serif text-xl md:text-2xl text-[#1C2B3A]/80 mb-3" data-testid="hero-title">
                {personalInfo.title}
              </p>
              <p className="font-sans text-[#7A7A7A] text-base leading-relaxed max-w-xl mb-10">{personalInfo.tagline}</p>
              <div className="flex flex-wrap gap-4">
                <Button asChild className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white px-7 py-2.5 rounded font-sans text-sm tracking-wider h-auto" data-testid="view-profile-btn"><Link to="/about">View Profile</Link></Button>
                <Button asChild variant="outline" className="border-[#1C2B3A] text-[#1C2B3A] hover:bg-[#1C2B3A] hover:text-white px-7 py-2.5 rounded font-sans text-sm tracking-wider h-auto" data-testid="get-in-touch-btn"><Link to="/contact">Get in Touch</Link></Button>
              </div>
            </div>
            <div className="lg:col-span-2 hidden lg:block animate-fade-up-delay">
              <div className="space-y-10 pl-12 border-l-2 border-[#E5DDD3]">
                {[{ value: stats.countries, label: "Countries" }, { value: stats.clients, label: "Clients" }, { value: stats.yearsExperience, label: "Years Experience" }].map((s) => (
                  <div key={s.label}>
                    <p className="text-5xl font-serif font-bold text-[#B8945A]">{s.value}</p>
                    <p className="text-xs tracking-[0.2em] uppercase text-[#7A7A7A] font-sans mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <NewsTicker />

      {/* About Preview */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative overflow-hidden rounded-lg aspect-[4/3] group">
              {heroImages.length > 0 ? (
                <ImageCarousel images={heroImages} alt="International consulting" testid="home-hero-carousel" className="group-hover:scale-105 transition-transform duration-700" />
              ) : (
                <img src={images.consulting} alt="International consulting" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              )}
            </div>
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">About</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C2B3A] mb-6">{personalInfo.title}</h2>
              <p className="font-sans text-[#4A4A4A] leading-relaxed mb-8">{personalInfo.shortBio}</p>
              <Link to="/about" className="inline-flex items-center gap-2 text-[#B8945A] hover:text-[#96783E] font-sans text-sm tracking-wider uppercase transition-colors duration-200 group">Learn More <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Global Footprint */}
      <section className="py-24 bg-[#FAF8F4]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Global Presence</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C2B3A] mb-8">Global Footprint</h2>
            <div className="flex justify-center gap-10 md:gap-16">
              {[{ value: stats.countries, label: "Countries" }, { value: stats.clients, label: "Clients" }, { value: stats.yearsExperience, label: "Years" }].map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-2xl md:text-3xl font-serif font-bold text-[#1C2B3A]">{s.value}</p>
                  <p className="text-xs tracking-wider uppercase text-[#7A7A7A] font-sans mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="max-w-5xl mx-auto"><WorldMap /></div>
          <p className="text-center text-sm text-[#7A7A7A] font-sans mt-6 italic">Hover over highlighted countries to explore</p>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C2B3A] mb-14">Areas of Practice</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {expertise.map((item) => {
              const Icon = iconMap[item.icon] || Globe;
              return (
                <div key={item.name} className="group p-5 rounded-lg border border-[#E5DDD3] bg-white hover:border-[#B8945A]/60 hover:shadow-md transition-all duration-300">
                  <Icon size={22} className="text-[#B8945A] mb-3 group-hover:scale-110 transition-transform duration-300" />
                  <p className="font-sans text-sm text-[#1C2B3A] font-medium leading-snug">{item.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clients Marquee */}
      <section className="py-24 bg-[#FAF8F4] overflow-hidden" data-testid="clients-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Collaborations</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C2B3A]">Notable Clients &amp; Partners</h2>
          </div>
        </div>
        <div className="relative group">
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#FAF8F4] to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#FAF8F4] to-transparent z-10 pointer-events-none"></div>
          <div
            className="flex gap-8 animate-marquee group-hover:[animation-play-state:paused]"
            style={{ width: "max-content", animationDuration: `${Math.max(clients.length * 4, 60)}s` }}
          >
            {[...clients, ...clients].map((client, i) => {
              const card = (
                <div className="flex items-center justify-center h-[110px] w-[170px] px-5 rounded-lg border border-[#E5DDD3] bg-white hover:border-[#B8945A]/50 hover:shadow-md transition-all duration-200 flex-shrink-0" title={client.name} data-testid={`client-logo-${i}`}>
                  {client.logo_path ? (
                    <img src={getLogoUrl(client.logo_path)} alt={client.short || client.name} className="max-w-full max-h-full object-contain" loading="lazy" />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-[#7A7A7A]">
                      <Building2 size={28} className="text-[#D5CFC7]" />
                      <span className="font-serif text-xs text-[#1C2B3A]/60 whitespace-nowrap">{client.short || client.name}</span>
                    </div>
                  )}
                </div>
              );
              return client.url ? (
                <a key={`${client.name}-${i}`} href={client.url} target="_blank" rel="noopener noreferrer" className="no-underline">{card}</a>
              ) : (
                <div key={`${client.name}-${i}`}>{card}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1C2B3A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">The Right Conversation Starts Here</h2>
          <p className="font-sans text-white/60 leading-relaxed mb-10 max-w-xl mx-auto">If the corridors connecting India, Africa and Asia matter to your next move — this is where that conversation begins.</p>
          <Button asChild className="bg-[#B8945A] hover:bg-[#96783E] text-white px-8 py-3 rounded font-sans text-sm tracking-wider h-auto" data-testid="cta-btn"><Link to="/contact">Get in Touch</Link></Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
